import React from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar, { SidebarNav } from '../Sidebar'
import { useAuthContext } from '../../contexts/auth'
import { FuncionalidadesGeral } from '../../helpers/funcionalidades'
import { HeaderProvider } from '../../contexts/HeaderContext'
import { GlobalNotification } from '../GlobalNotification'
import { options as optionsAtendimento } from '../../pages/Dashboard/Atendimento/options'
import { options as optionsCriadorFormulario } from '../../pages/Dashboard/CriadorFormulario/options'
import { options as optionsCaixaEntrada } from '../../pages/Dashboard/CaixaEntrada/options'
import { options as optionsRelatorios, ROTA_ABRIR_RELATORIO_ESTATISTICO } from '../../pages/Dashboard/Relatorios/options'
import iFormulario from '../../images/grid-v.png'
import Footer from '../Footer'
import ImagemLogo from '../../images/logo_ouvidoria_branco_horizontal.png'
import Header from '../Header'
import './style.scss'

interface LayoutDefaultProps {
  children: React.ReactNode
}

const LayoutDefault = ({ children }: LayoutDefaultProps) => {
  const { pathname } = useLocation()
  const telaAtendimento = pathname === '/consultar-atendimentos' || /^\/atendimento\/[^/]+\/?$/.test(pathname)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const { handleHasPermission, user, switchProfile } = useAuthContext()
  const generateSidebarItems = (): SidebarNav[] => {
    const items: SidebarNav[] = []

    if (user?.activeProfile?.roles) {
      const hasAtendimentoPermissions =
        handleHasPermission(FuncionalidadesGeral.atendimento.novoAtendimentoPresencial) ||
        handleHasPermission(FuncionalidadesGeral.atendimento.novoAtendimentoTelefonico) ||
        handleHasPermission(FuncionalidadesGeral.atendimento.consultarAtendimentos) ||
        handleHasPermission(FuncionalidadesGeral.caixaEntrada.complementarInformacao)
      
      if (hasAtendimentoPermissions) {
        const filteredAtendimentoOptions = optionsAtendimento
          .filter(option => {
            return handleHasPermission(option.funcionalidade)
          })
          .sort((a, b) => a.order - b.order)

        const atendimentoItems = [
          {
            id: 1,
            title: 'Atendimento',
            path: '/',
            icon: iFormulario,
            hasPermission: hasAtendimentoPermissions,
            subNav: filteredAtendimentoOptions.map((option, index) => ({
              id: option.order || index + 1,
              title: option.title,
              path: option.route,
              icon: option.icon,
              hasPermission: handleHasPermission(option.funcionalidade),
              area: 'atendimento'
            }))
          }]

        items.push(...atendimentoItems)
      }

      const hasFormularioPermissions = 
        handleHasPermission(FuncionalidadesGeral.formularios.consultar) ||
        handleHasPermission(FuncionalidadesGeral.secoes.consultar) ||
        handleHasPermission(FuncionalidadesGeral.perguntas.consultar) ||
        handleHasPermission(FuncionalidadesGeral.servicos.consultar) ||
        handleHasPermission(FuncionalidadesGeral.assuntos.consultar) ||
        handleHasPermission(FuncionalidadesGeral.dominios.consultar) ||
        handleHasPermission(FuncionalidadesGeral.metodos.consultar)

      if (hasFormularioPermissions) {
        const filteredFormularioOptions = optionsCriadorFormulario
          .filter(option => {
            return handleHasPermission(option.funcionalidade) && option.area === 'formulario'
          })
          .sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'))

        const formularioItems = [
          {
            id: 2,
            title: 'Construtor de Formulários',
            path: '/',
            icon: iFormulario,
            hasPermission: hasFormularioPermissions,
            subNav: filteredFormularioOptions.map((option, index) => ({
              id: option.title.localeCompare(option.title, 'pt-BR') + 10 || index + 11,
              title: option.title,
              path: option.route,
              icon: option.icon,
              hasPermission: handleHasPermission(option.funcionalidade),
              area: 'formulario'
            }))
          }]

        items.push(...formularioItems)
      }

      const hasCaixaEntradaPermissions = handleHasPermission(FuncionalidadesGeral.caixaEntrada.consultar)
      const showCaixaEntrada = hasCaixaEntradaPermissions
      if (showCaixaEntrada) {
        const filteredCaixaEntradaOptions = optionsCaixaEntrada
          .filter(option => !option.funcionalidade || handleHasPermission(option.funcionalidade))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

        const caixaEntradaItems = [
          {
            id: 3,
            title: 'Análise',
            path: '/',
            icon: iFormulario,
            hasPermission: showCaixaEntrada,
            subNav: filteredCaixaEntradaOptions.map((option, index) => ({
              id: 30 + (option.order ?? index),
              title: option.title,
              path: option.route,
              icon: option.icon,
              hasPermission: !option.funcionalidade || handleHasPermission(option.funcionalidade),
              area: 'caixa_entrada'
            }))
          }]
        items.push(...caixaEntradaItems)
      }

      // Menu de Comunicação (sempre visível se tiver permissão)
      const hasRegistroComunicacaoPermissions = handleHasPermission(
        FuncionalidadesGeral.registroComunicacao.cadastrarManualmente,
      )
      if (hasRegistroComunicacaoPermissions) {
        items.push({
          id: 12,
          title: 'Registro de Comunicação',
          // Bug 856501: o grupo nasceu sem ícone e o <img src=''> do Sidebar
          // renderizava imagem quebrada. Usa o mesmo dos demais grupos.
          icon: iFormulario,
          hasPermission: hasRegistroComunicacaoPermissions,
          subNav: [
            {
              // HU-008: Registrar Comunicação Manualmente.
              id: 14,
              title: 'Registro Manual',
              path: '/registro-manual',
              icon: '',
              hasPermission: hasRegistroComunicacaoPermissions,
            },
          ],
        })
      }

      // HU-023: Infográficos e Relatórios. O item leva para a página inicial com o marcador
      // que abre o modal de período — o relatório não tem tela própria, é o modal e o PDF.
      // CP11 pede perfil autorizado; enquanto a pendência PD04 não define qual, vale também
      // para quem enxerga a Caixa de Entrada.
      const podeEmitirRelatorio =
        handleHasPermission(FuncionalidadesGeral.relatorios.estatisticoComunicacao) ||
        hasCaixaEntradaPermissions
      if (podeEmitirRelatorio) {
        items.push({
          id: 20,
          title: 'Infográficos e Relatórios',
          icon: iFormulario,
          hasPermission: podeEmitirRelatorio,
          subNav: optionsRelatorios.map((option, index) => ({
            id: 200 + index,
            title: option.title,
            path: ROTA_ABRIR_RELATORIO_ESTATISTICO,
            icon: '',
            hasPermission: podeEmitirRelatorio,
          })),
        })
      }

      // HU-011 / Figma 365:4992: ordem funcional dos grupos do menu.
      const ordemGrupos = ['Atendimento', 'Análise', 'Registro de Comunicação',
        'Construtor de Formulários', 'Infográficos e Relatórios']
      const indiceGrupo = (titulo: string) => {
        const indice = ordemGrupos.indexOf(titulo)
        return indice >= 0 ? indice : ordemGrupos.length
      }
      return items.sort((a, b) => indiceGrupo(a.title) - indiceGrupo(b.title))
    }
    // Retornar array vazio se não há usuário ativo
    return []
  }

  const sidebarItems: SidebarNav[] = React.useMemo(() => {
    return generateSidebarItems()
  }, [user?.activeProfile?.id, user?.activeProfile?.roles])

  const handleSwitchProfile = (area: string) => {
    return
  }

  return (
    <HeaderProvider>
      <div className={`layout__wrapper${telaAtendimento ? ' layout__wrapper--atendimentos' : ''}`}>
        <div className='container'>
          <Sidebar
            isOpen={menuOpen}
            onClose={() => setMenuOpen(!menuOpen)}
            logoText='MPRJ | SGO'
            href='/'
            items={sidebarItems}
            onSwitchProfile={handleSwitchProfile}
          />
          <Header
            headerTitleProps={{
              logoImg: (
                <img
                  src={ImagemLogo}
                  alt='Ouvidoria MPRJ'
                  className='header-logo-img'
                />
              ),
              href: '/',
            }}
            handleOpenMenu={() => setMenuOpen(!menuOpen)}
          />
          <div className='content-main'>{children}</div>
          <Footer />
          <GlobalNotification />
        </div>
      </div>
    </HeaderProvider>
  )
}

export default LayoutDefault
